import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from '../lib/cn';
import { Dot, type Tone } from './Status';

const PROGRESS_FILL: Record<Tone, string> = {
  neutral: 'bg-accent',
  accent: 'bg-accent',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
};

export interface StatProps {
  label: string;
  icon?: LucideIcon;
  value: ReactNode;
  note?: ReactNode;
  /** Kolor kropki przy notatce (i paska postępu). Bez kropki: `showDot={false}`. */
  tone?: Tone;
  showDot?: boolean;
  /** 0–1: pasek postępu pod wartością (np. 32 z 40 h). */
  progress?: number;
  /** Wyróżniony kafelek (akcent): coś czeka na decyzję, np. „Do akceptacji”. */
  highlight?: boolean;
  /** Kafelek jako link do szczegółów. */
  to?: string;
  /**
   * Długa wartość (data i godzina, np. „15.10 · 10:00”): kafelek jest kontenerem (container query), a w wąskim kafelku
   * wartość ma 20 px zamiast 24 px — mieści się w całości przy 5 kafelkach w rzędzie (1280 px, czat Copilota obok).
   */
  fitValue?: boolean;
  className?: string;
}

/**
 * Kafelek liczby (Figma: Stat 3029:3031). Etykieta i ikona u góry, wartość 24 px, krótka notatka.
 * Kolor tylko w kropce notatki; wariant wyróżniony na akcencie.
 * Treść nigdy nie wychodzi poza ramkę: wiersze mają pełną szerokość kafelka (`items-stretch`), a etykieta,
 * wartość i notatka skracają się wielokropkiem. Na długą wartość (np. „8.10 · 10:00”) daj kafelkowi szerszą
 * komórkę (Figma mobile: „Najbliższe spotkanie” na pełną szerokość).
 */
export function Stat({
  label,
  icon: Icon,
  value,
  note,
  tone = 'neutral',
  showDot = true,
  progress,
  highlight = false,
  to,
  fitValue = false,
  className,
}: StatProps) {
  const body = (
    <>
      <div className="flex w-full items-center gap-2">
        <span className={cn('min-w-0 truncate text-caption font-medium', highlight ? 'text-accent' : 'text-fg-muted')}>
          {label}
        </span>
        <span className="flex-1" />
        {Icon && (
          <Icon
            size={16}
            strokeWidth={1.75}
            aria-hidden
            className={cn('shrink-0', highlight ? 'text-accent' : 'text-fg-muted')}
          />
        )}
      </div>
      <div
        className={cn(
          'mt-3.5 min-w-0 truncate font-semibold tabular',
          fitValue ? 'text-h4 @min-[11rem]:text-h3' : 'text-h3',
          highlight ? 'text-accent' : 'text-fg',
        )}
      >
        {value}
      </div>
      {progress !== undefined && (
        <div
          className="mt-2.5 h-1 w-full overflow-hidden rounded-[2px] bg-elevated"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(Math.min(1, Math.max(0, progress)) * 100)}
          aria-label={label}
        >
          <div
            className={cn('h-full rounded-[2px]', PROGRESS_FILL[tone])}
            style={{ width: `${Math.min(1, Math.max(0, progress)) * 100}%` }}
          />
        </div>
      )}
      {note !== undefined && note !== null && (
        <div className="mt-1.5 flex min-w-0 items-center gap-1.5 text-caption text-fg-secondary">
          {showDot && <Dot tone={tone} size={6} />}
          <span className="min-w-0 truncate">{note}</span>
        </div>
      )}
    </>
  );

  const classes = cn(
    'flex min-w-0 flex-col items-stretch rounded-xl border py-4 pr-4 pl-[18px] text-left',
    fitValue && '@container',
    highlight ? 'border-accent bg-accent-subtle' : 'border-line bg-panel',
    to && 'transition-colors hover:border-line-strong focus-visible:outline-2 focus-visible:outline-accent',
    to && highlight && 'hover:border-accent-hover',
    className,
  );

  if (to) {
    return (
      <Link to={to} className={classes}>
        {body}
      </Link>
    );
  }
  return <div className={classes}>{body}</div>;
}
